import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Typography, TextField, Paper, Grid } from "@mui/material";
import Logo from "../../components/common/Logo";
import loginBG from "../../assets/images/login_background.png";
import { useDispatch } from "react-redux";

import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Notification from "../../components/common/notification/Notification";
import { setNotificationAction } from "../../store/reducers/mmymSlice";
import { persistor } from "../../store/store";
import {
  sendCrewOtpAction,
  setSendCrewOtpAction,
  setVerifyCrewOtpAction,
  urlVerifyInventoryCrewLinkAction,
  verifyCrewOtpAction,
} from "../../store/reducers/inventoryCrewSlice";

interface CrewInventoryOtpLoginProps {
  clientName?: string;
  custName?: string;
  custEmail?: string;
  custMobile?: string;
  expired?: boolean;
}

const defaultProps = {
  clientName: "Your Company",
  custName: "Customer Name",
  custEmail: "customer@email.com",
  custMobile: "+1234567890",
  expired: false,
};

const CrewInventoryOtpLogin = (props: CrewInventoryOtpLoginProps) => {
  const { clientName, custName, custEmail, custMobile, expired } = {
    ...defaultProps,
    ...props,
  };
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  const token = searchParams.get("token");
  const tenantId = searchParams.get("tenant_id");
  const contractId = searchParams.get("contract_id");
  const {
    verifyInventoryCrewLinkResponse,
    isCrewTokenInValid,
    isCrewSendOtpSuccess,
    isVerifyCrewOtpSuccess,
  } = useSelector((state: any) => state.inventoryCrew || {});

  useEffect(() => {
    // clear session storage and local storage on load of this page
    if (location.pathname === "/customer") {
      persistor.purge(); // 🔥 clears redux persist
      sessionStorage.clear();
      localStorage.clear();
      dispatch(setNotificationAction({ message: [], severity: "error" }));
      dispatch(setVerifyCrewOtpAction(false));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (token && tenantId && contractId) {
      dispatch(
        urlVerifyInventoryCrewLinkAction({
          token,
          tenantId,
          contractId,
        }),
      );
    }
    // dispatch(setResentLinkCustomerPortalAction(false));
    dispatch(setNotificationAction({ message: [], severity: "error" }));
  }, [token, tenantId, contractId]);

  // useEffect(() => {
  //   if (isResentLinkCustomerPortal) {
  //     dispatch(setResentLinkCustomerPortalAction(false));
  //     // alert("A new link has been sent to your registered email and mobile number.");
  //   }
  // }, [isResentLinkCustomerPortal]);

  useEffect(() => {
    if (isVerifyCrewOtpSuccess) {
      dispatch(setVerifyCrewOtpAction(false));
      dispatch(setSendCrewOtpAction(false));
      dispatch(setNotificationAction({ message: [], severity: "error" }));
      navigate(`/inventory-dashboard?token=${token}&tenant_id=${tenantId}&contract_id=${contractId}`);
    }
  }, [dispatch, isVerifyCrewOtpSuccess, location.search, navigate]);

  // const handleResentLink = () => {
  //   dispatch(
  //     resentLinkCustomerPortalAction({
  //       token,
  //       tenant_id: tenantId,
  //       customer_id: contractId,
  //     }),
  //   );
  // };

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(30);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [otpMethod, setOtpMethod] = useState<string>("");
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // const handleLogin = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   const otpString = otp.join("");
  //   console.log("OTP entered:", otpString);
  //   if (otp.some((digit) => digit === "")) {
  //     setError("Please enter the 6-digit OTP");
  //     return;
  //   }
  //   setError("");
  //   // TODO: Call API to verify OTP and login with otpString
  //   dispatch(
  //     verifyOtpAction({
  //       tenantId,
  //       contractId,
  //       token,
  //       otp: otpString,
  //     }),
  //   );
  // };

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number,
  ) => {
    const value = e.target.value;
    if (!/^[0-9]?$/.test(value)) return; // Only allow single digit
    const newOtp = [...otp];
    newOtp[idx] = value;
    setOtp(newOtp);
    if (value && idx < 5) {
      (inputRefs[idx + 1].current as any)?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number,
  ) => {
    if (e.key === "Backspace" && otp[idx] === "" && idx > 0) {
      (inputRefs[idx - 1].current as any)?.focus();
    }
  };

  const startCountdown = useCallback(() => {
    setCountdown(30);
    setIsCountdownActive(true);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          setIsCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  // Start countdown when OTP is sent successfully
  useEffect(() => {
    if (isCrewSendOtpSuccess) {
      startCountdown();
    }
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [isCrewSendOtpSuccess, startCountdown]);

  const handleSendOtp = (method: string) => () => {
    setOtpMethod(method);
    const payload = {
      tenantId,
      contractId,
      token,
      method,
    };
    dispatch(sendCrewOtpAction(payload));
  };

  const handleResendOtp = () => {
    if (!otpMethod) return;
    setOtp(["", "", "", "", "", ""]);
    setError("");
    dispatch(
      sendCrewOtpAction({ tenantId, contractId, token, method: otpMethod }),
    );
    startCountdown();
  };

  const handleBack = () => {
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setCountdown(30);
    setIsCountdownActive(false);
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    dispatch(setSendCrewOtpAction(false));
  };

   const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    const otpString = otp.join("");
    console.log("OTP entered:", otpString);
    if (otp.some((digit) => digit === "")) {
      setError("Please enter the 6-digit OTP");
      return;
    }
    setError("");
    // TODO: Call API to verify OTP and login with otpString
    dispatch(verifyCrewOtpAction({
      tenantId,
      contractId,
      token,
      otp: otpString,
    }));
  };
  // const handleLogin = () => {
  //   navigate("/inventory-dashboard");
  // };

  return (
    <Grid
      container
      component="main"
      sx={{
        height: "100vh",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
        zIndex: 1,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${loginBG})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 1,
          zIndex: -1,
        },
      }}
    >
      <Grid
        item
        xs={12}
        sm={12}
        md={6}
        component={Paper}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          maxWidth: "100%",
          backgroundColor: "rgba(255, 255, 255, 0.50)",
          padding: "16px 0px",
          borderRadius: "25px",
        }}
      >
        <Notification />
        {isCrewTokenInValid && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: { xs: "16px", sm: "32px" },
              width: "80%",
            }}
          >
            <Logo isTitle={true} isPosition={true} />
            {/* <Typography
              variant="h5"
              fontWeight={600}
              align="center"
              sx={{ color: "#d32f2f", mt: 3, mb: 2 }}
            >
              Invalid or Expired Link
            </Typography> */}
            {/* <Typography
              align="center"
              sx={{ color: "#333", mb: 3, fontSize: "16px", fontWeight: 600 }}
            >
              {customerLinkErrorResponse?.message ||
                "Too many access attempts. Please contact your removal company."}
            </Typography> */}
            {/* <Button
              variant="contained"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                borderRadius: "20px",
                padding: "10px 24px",
                textTransform: "none !important",
                backgroundColor: "#076C6B",
                letterSpacing: 1.5,
              }}
              href="/"
            >
              Go To Login
            </Button> */}
          </Box>
        )}

        {!isCrewTokenInValid &&
          verifyInventoryCrewLinkResponse?.client_name && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: { xs: "0px 16px", sm: "0px" },
                width: "80%",
              }}
            >
              <Logo isTitle={true} isPosition={true} />
              <Typography
                variant="h1"
                fontWeight={600}
                align="center"
                sx={{
                  fontSize: { xs: "2rem", sm: "3rem" },
                  color: "#000000",
                  mb: 2,
                }}
              >
                Crew Login
              </Typography>

              <Typography
                variant="h5"
                fontWeight={600}
                align="center"
                sx={{ color: "#000000", mb: 2 }}
              >
                {verifyInventoryCrewLinkResponse?.is_expired
                  ? `${verifyInventoryCrewLinkResponse?.company_name} crew portal`
                  : `You are about to login to ${verifyInventoryCrewLinkResponse?.company_name}`}
              </Typography>
              <Typography
                variant="h6"
                align="center"
                sx={{ color: "#000000", mb: 2 }}
              >
                Hello{" "}
                {verifyInventoryCrewLinkResponse?.crew_name ??
                  verifyInventoryCrewLinkResponse?.client_name}
              </Typography>
              {verifyInventoryCrewLinkResponse?.is_expired ? (
                <>
                  <Typography align="center" sx={{ color: "#000000", mb: 2 }}>
                    This link has expired please request a new link
                  </Typography>
                  {/* <Button
                    variant="contained"
                    sx={{
                      fontWeight: 600,
                      fontSize: "18px",
                      borderRadius: "20px",
                      padding: "10px 16px",
                      textTransform: "none !important",
                      backgroundColor: "#076C6B",
                      letterSpacing: 1.5,
                      mb: 2,
                    }}
                    onClick={handleResentLink}
                  >
                    Click here to request a new link
                  </Button> */}
                </>
              ) : (
                <>
                  {!isCrewSendOtpSuccess && (
                    <>
                      {verifyInventoryCrewLinkResponse?.is_subscription_expired ? (
                        <Typography
                          align="center"
                          sx={{ color: "#9b0707", fontWeight: 600, mb: 2 }}
                        >
                          {verifyInventoryCrewLinkResponse?.message}
                        </Typography>
                      ) : (
                        <Typography
                          align="center"
                          sx={{ color: "#000000", mb: 2 }}
                        >
                          To verify your identity please click one of the links
                          below to get your one time pass code.
                        </Typography>
                      )}
                      {verifyInventoryCrewLinkResponse?.crew_email && (
                        <Button
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            borderRadius: "20px",
                            padding: "8px 16px",
                            textTransform: "none !important",
                            borderColor: "#076C6B",
                            color: "#076C6B",
                            mb: 1,
                          }}
                          onClick={handleSendOtp("email")}
                        >
                          Send an email to{" "}
                          {verifyInventoryCrewLinkResponse?.crew_email}
                        </Button>
                      )}
                      {verifyInventoryCrewLinkResponse?.crew_phone && (
                        <Button
                          variant="outlined"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            borderRadius: "20px",
                            padding: "8px 16px",
                            textTransform: "none !important",
                            borderColor: "#076C6B",
                            color: "#076C6B",
                            mb: 2,
                          }}
                          onClick={handleSendOtp("sms")}
                        >
                          Send a text to{" "}
                          {verifyInventoryCrewLinkResponse?.crew_phone}
                        </Button>
                      )}
                    </>
                  )}

                  {isCrewSendOtpSuccess && (
                    <>
                      <Typography
                        align="center"
                        sx={{ color: "#000000", mb: 2 }}
                      >
                        When you receive your OTP enter it here.
                        <br />
                        <span style={{ fontWeight: 600 }}>
                          It is time sensitive, so don't delay
                        </span>
                      </Typography>
                      <Box
                        component="form"
                        noValidate
                        onSubmit={handleLogin}
                        sx={{ mt: 1, width: "100%" }}
                      >
                        <Box
                          display="flex"
                          justifyContent="center"
                          gap={2}
                          mt={2}
                          mb={2}
                        >
                          {otp.map((digit, idx) => (
                            <TextField
                              key={idx}
                              inputRef={inputRefs[idx]}
                              value={digit}
                              onChange={(e: any) => handleOtpChange(e, idx)}
                              onKeyDown={(e: any) => handleOtpKeyDown(e, idx)}
                              inputProps={{
                                maxLength: 1,
                                style: {
                                  textAlign: "center",
                                  fontSize: "1rem",
                                  width: "16px",
                                  height: "16px",
                                  backgroundColor: "#FFFFFF",
                                  overflow: "hidden",
                                  borderRadius: "5px",
                                },
                              }}
                              variant="outlined"
                              required
                            />
                          ))}
                        </Box>
                        {error && (
                          <Typography
                            color="error"
                            variant="body2"
                            mt={1}
                            align="center"
                          >
                            {error}
                          </Typography>
                        )}
                        <div style={{ textAlign: "center" }}>
                          <Button
                            // type="submit"
                            onClick={handleLogin}
                            fullWidth
                            variant="contained"
                            sx={{
                              fontWeight: 600,
                              fontSize: "18px",
                              mt: 3,
                              mb: 2,
                              width: "160px",
                              borderRadius: "20px",
                              padding: "10px 16px",
                              textTransform: "none !important",
                              backgroundColor: "#076C6B",
                              letterSpacing: 1.5,
                            }}
                          >
                            Login
                          </Button>
                        </div>
                        {isCountdownActive ? (
                          <Typography
                            align="center"
                            sx={{ color: "#076C6B", fontWeight: 600, mb: 1 }}
                          >
                            Resend OTP in {countdown}s
                          </Typography>
                        ) : (
                          <></>
                        )}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Box>
          )}
      </Grid>
    </Grid>
  );
};

export default CrewInventoryOtpLogin;
